/// <reference path="../../css.d.ts" />
"use client";

// Source : UIArc `registry/components/popover` (MIT © 2026 Elia Kuratli), commit 7238f98196b147a8d50ce467abcbad310d4d8414.
// Adaptations UBM (lot U2) :
//   · Root à OUVERTURE LOGIQUE : contrôlé ou non, il rend à Radix `open && engagé`. Sous un hôte désengagé (fenêtre
//     derrière ou masquée), le panneau en portail se retire — il quitte la pile Radix, plus de portail flottant au-dessus
//     de la fenêtre active — sans notifier `onOpenChange` ; il revient ouvert au réengagement. (L'état INTERNE du contenu
//     n'est pas conservé à travers le désengagement ; un contenu à brouillon relève d'un Dialog ou d'un peek.)
//   · Content : marque de couche non modale de la pile commune (`@ubm/ui/overlays`) et pont d'Échap (une couche non
//     Radix au-dessus garde son Échap).
//   · `Anchor` exporté (ancre distincte du déclencheur, comme Radix) pour les compositions.
// Rendu, styles et mouvements : ceux de la source.
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { createContext, forwardRef, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from "react";
import { useReducedMotionConfig } from "motion/react";
import { useOverlayHostEngaged, cederEchap } from "../../overlays/OverlayHost";
import { estOverlayDuDessus, marqueOverlayNonModal } from "../../overlays/useOverlayAccessible";
import "../../styles/foundation.css";
import styles from "./popover.module.css";

/**
 * Classes de la peau source, pour les façades qui rendent le même panneau sur le moteur non modal commun. `arrow` (lot E) :
 * la même règle de fond que `content`, pour la flèche Radix d'une façade — peinture seule, la géométrie reste à la façade.
 */
export const popoverClassNames = { content: styles.content as string, anchor: styles.anchor as string, arrow: styles.arrow as string };

const PopoverEngagement = createContext(true);

export interface PopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  children?: ReactNode;
}

export function Popover({ open: openProp, defaultOpen = false, onOpenChange, modal, children }: PopoverProps) {
  const engaged = useOverlayHostEngaged();
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = openProp ?? uncontrolled;
  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolled(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  return (
    <PopoverEngagement.Provider value={engaged}>
      <PopoverPrimitive.Root open={open && engaged} onOpenChange={setOpen} modal={modal}>
        {children}
      </PopoverPrimitive.Root>
    </PopoverEngagement.Provider>
  );
}

export const PopoverClose = PopoverPrimitive.Close;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverPortal = PopoverPrimitive.Portal;
/** Lot A5 — flèche Radix, réexport pur (décoration positionnée par le moteur Popper ; aucune logique ajoutée). */
export const PopoverArrow = PopoverPrimitive.Arrow;

/** The trigger anchors the panel, so it opts out of press-scale: a scaled rect measured on open would shift the panel as the trigger springs back. */
export const PopoverTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(function PopoverTrigger({ className, ...props }, ref) {
  return <PopoverPrimitive.Trigger {...props} ref={ref} className={[styles.anchor, className].filter(Boolean).join(" ")}/>;
});

PopoverTrigger.displayName = "PopoverTrigger";

/** Contenu bas niveau (sans portail ni peau) : marque de couche commune + pont d'Échap. */
export const PopoverContentBase = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContentBase({ onEscapeKeyDown, ...props }, ref) {
  const engaged = useContext(PopoverEngagement);
  const reduced = useReducedMotionConfig();
  const node = useRef<HTMLDivElement | null>(null);
  const composed = useMemo(
    () => (el: HTMLDivElement | null) => {
      node.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  return <PopoverPrimitive.Content
    {...marqueOverlayNonModal}
    data-motion={reduced ? "reduced" : undefined}
    {...props}
    ref={composed}
    onEscapeKeyDown={event => {
      onEscapeKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (!engaged || (node.current !== null && !estOverlayDuDessus(node.current))) cederEchap(event);
    }}
  />;
});

PopoverContentBase.displayName = "PopoverContentBase";

export const PopoverContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent({ className, align = "start", sideOffset = 6, collisionPadding = 10, ...props }, ref) {
  return <PopoverPrimitive.Portal>
    <PopoverContentBase
      {...props}
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={[styles.content, className].filter(Boolean).join(" ")}
    />
  </PopoverPrimitive.Portal>;
});

PopoverContent.displayName = "PopoverContent";
